import { cn } from '@/platform/lib/utils';
import { TabNav, TabNavItem } from '@/platform/ui';
import { XCircleIcon } from '@heroicons/react/24/outline';
import {
    CheckCircleIcon as SolidCheckCircleIcon,
    ClockIcon as SolidClockIcon,
    XCircleIcon as SolidXCircleIcon,
} from '@heroicons/react/24/solid';
import { Link, usePage } from '@inertiajs/react';

const ExistsIcon = ({ exists, invalid = false }) => {
    if (invalid) {
        return <SolidXCircleIcon className='size-4 shrink-0 text-red-600' />;
    }

    return exists ? (
        <SolidCheckCircleIcon className='size-4 shrink-0 text-green-600' />
    ) : (
        <XCircleIcon className='size-4 shrink-0 text-gray-400' />
    );
};

const StatusIcon = ({ completed, inProgress, failed }) => {
    if (completed) {
        return (
            <SolidCheckCircleIcon className='size-4 shrink-0 text-green-600' />
        );
    }

    if (inProgress) {
        return <SolidClockIcon className='size-4 shrink-0 text-yellow-500' />;
    }

    if (failed) {
        return <SolidXCircleIcon className='size-4 shrink-0 text-red-600' />;
    }

    return <XCircleIcon className='size-4 shrink-0 text-gray-400' />;
};

const DgrIcon = ({ check }) =>
    check ? (
        <SolidCheckCircleIcon
            className={cn(
                'size-4 shrink-0',
                check.status ? 'text-green-600' : 'text-red-600'
            )}
        />
    ) : (
        <XCircleIcon className='size-4 shrink-0 text-gray-400' />
    );

const useNavigationItems = () => {
    const { auth, navigation } = usePage().props;
    const { component } = usePage();
    const permissions = auth?.permissions ?? [];
    const base = `/customers/${auth?.user?.selected_customer_id}/employees/${navigation.employee_id}`;

    return [
        {
            label: 'Werknemer',
            href: base,
            component: 'Employees/ShowView',
            visible: permissions.includes('view employees'),
        },
        {
            label: 'DGR',
            href: `${base}/dgr`,
            component: 'Employees/DgrView',
            visible: permissions.includes('view dgr'),
            icon: <DgrIcon check={navigation?.latest_dgr_check} />,
        },
        {
            label: 'Vragenlijsten',
            href: `${base}/questionnaires`,
            component: 'Employees/QuestionnaireView',
            visible: permissions.includes('view questionnaires'),
            icon: (
                <StatusIcon
                    completed={navigation?.has_completed_questionnaires}
                    inProgress={navigation?.has_in_progress_questionnaires}
                    failed={navigation?.has_failed_questionnaires}
                />
            ),
        },
        {
            label: 'Machtigingen',
            href: `${base}/authorisations`,
            component: 'Employees/AuthorisationView',
            visible: permissions.includes('view signhost'),
            icon: (
                <StatusIcon
                    completed={navigation?.has_signed_authorisations}
                    inProgress={navigation?.has_in_progress_authorisations}
                    failed={navigation?.has_failed_authorisations}
                />
            ),
        },
        {
            label: 'Communicatie',
            href: `${base}/communications`,
            component: 'Employees/CommunicationView',
            visible: true,
            icon: (
                <ExistsIcon
                    exists={navigation?.has_communications}
                    invalid={navigation?.has_invalid_communications}
                />
            ),
        },
        {
            label: 'Documenten',
            href: `${base}/documents`,
            component: 'Employees/DocumentView',
            visible: permissions.includes('view documents'),
        },
        {
            label: 'LKV',
            href: `${base}/lkv`,
            component: 'Employees/LkvView',
            visible: permissions.includes('view lkv'),
            icon: <ExistsIcon exists={navigation?.has_lkv} />,
        },
        {
            label: 'SROI',
            href: `${base}/sroi`,
            component: 'Employees/SroiView',
            visible: permissions.includes('view sroi'),
            icon: <ExistsIcon exists={navigation?.has_sroi} />,
        },
        {
            label: 'NoRiskpolis',
            href: `${base}/no-risk`,
            component: 'Employees/NoRiskView',
            visible: permissions.includes('view no-risk'),
            icon: <ExistsIcon exists={navigation?.has_no_risk} />,
        },
    ]
        .filter(item => item.visible)
        .map(item => ({ ...item, active: item.component === component }));
};

const EmployeeNavigation = () => {
    const items = useNavigationItems();

    return (
        <>
            <nav
                aria-label='Werknemer'
                className='hidden w-56 shrink-0 lg:sticky lg:top-16 lg:block lg:self-start'
            >
                <ul className='flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white py-2 shadow-sm'>
                    {items.map(item => (
                        <li key={item.href}>
                            <Link
                                href={item.href}
                                aria-current={item.active ? 'page' : undefined}
                                className={cn(
                                    'flex items-center gap-x-3 border-l-4 px-4 py-2 text-sm/6 transition-colors duration-200',
                                    item.active
                                        ? 'border-primary-600 bg-primary-50 text-primary-600'
                                        : 'border-transparent text-gray-500 hover:border-gray-500 hover:bg-gray-50 hover:text-gray-700'
                                )}
                            >
                                {item.label}
                                {item.icon && (
                                    <span className='ml-auto'>{item.icon}</span>
                                )}
                            </Link>
                        </li>
                    ))}
                </ul>
            </nav>

            <TabNav label='Werknemer' className='lg:hidden'>
                {items.map(item => (
                    <TabNavItem
                        key={item.href}
                        as={Link}
                        className='text-xs'
                        href={item.href}
                        active={item.active}
                        label={item.label}
                    >
                        <span className='inline-flex items-center gap-2'>
                            {item.label}
                            {item.icon}
                        </span>
                    </TabNavItem>
                ))}
            </TabNav>
        </>
    );
};

export default EmployeeNavigation;
